import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube, Linkedin } from "lucide-react";
import { COMPANY, PRODUCTS, IMAGES } from "@/data/content";
import { useEnquiry } from "@/context/EnquiryContext";
import { scrollToId } from "@/lib/scroll";

export default function Footer() {
  const { openEnquiry } = useEnquiry();
  const location = useLocation();
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const jump = (id) => (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 500);
    } else {
      scrollToId(id);
    }
  };

  return (
    <footer data-testid="footer" className="relative bg-brand-greenDark text-brand-cream/90 overflow-hidden">
      <div className="grain" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-20 pb-10">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img src={IMAGES.logo} alt="ADK SOLUTIONS logo" className="h-14 w-auto rounded-lg bg-brand-cream p-0.5" />
              <span className="font-heading font-extrabold tracking-tight text-xl text-white">ADK SOLUTIONS</span>
            </div>
            <p className="text-sm leading-relaxed text-brand-cream/70 max-w-xs">
              Premium agricultural machinery & farm implements for modern precision farming across India.
            </p>
            <div className="flex items-center gap-3 mt-6">
              {[
                { Icon: Facebook, href: COMPANY.socials.facebook, label: "facebook" },
                { Icon: Instagram, href: COMPANY.socials.instagram, label: "instagram" },
                { Icon: Youtube, href: COMPANY.socials.youtube, label: "youtube" },
                { Icon: Linkedin, href: COMPANY.socials.linkedin, label: "linkedin" },
              ].map(({ Icon, href, label }) => (
                <a key={label} href={href} data-testid={`social-${label}`} aria-label={label} className="grid place-items-center size-9 rounded-full border border-brand-cream/20 hover:bg-brand-cream hover:text-brand-greenDark transition-colors">
                  <Icon className="size-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-brand-cream/50 mb-4">Products</h4>
            <ul className="space-y-2.5 text-sm">
              {PRODUCTS.map((p) => (
                <li key={p.slug}>
                  <Link to={`/products/${p.slug}`} className="text-brand-cream/75 hover:text-white transition-colors">{p.name.replace("APOGEE ", "")}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-brand-cream/50 mb-4">Company</h4>
            <ul className="space-y-2.5 text-sm">
              <li><a href="/#about" onClick={jump("about")} className="text-brand-cream/75 hover:text-white transition-colors">About</a></li>
              <li><a href="/#gallery" onClick={jump("gallery")} className="text-brand-cream/75 hover:text-white transition-colors">Gallery</a></li>
              <li><a href="/#faq" onClick={jump("faq")} className="text-brand-cream/75 hover:text-white transition-colors">FAQs</a></li>
              <li><Link to="/blog" className="text-brand-cream/75 hover:text-white transition-colors">Blog</Link></li>
              <li><button onClick={() => openEnquiry("", "dealer")} data-testid="footer-dealer" className="text-brand-cream/75 hover:text-white transition-colors text-left">Become a Dealer</button></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-brand-cream/50 mb-4">Get in touch</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-3"><MapPin className="size-4 mt-0.5 shrink-0 text-brand-cream/60" /><span className="text-brand-cream/75">{COMPANY.address}</span></li>
              <li className="flex gap-3"><Phone className="size-4 shrink-0 text-brand-cream/60" /><a href={`tel:${COMPANY.phoneRaw}`} className="text-brand-cream/75 hover:text-white transition-colors">{COMPANY.phone}</a></li>
              <li className="flex gap-3"><Mail className="size-4 shrink-0 text-brand-cream/60" /><a href={`mailto:${COMPANY.email}`} className="text-brand-cream/75 hover:text-white transition-colors">{COMPANY.email}</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-brand-cream/15 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-brand-cream/60">
          <p>Copyright © {year} ADK SOLUTIONS. All Rights Reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
